import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);

    // Auto-reload on dynamic import failures (stale module cache)
    if (error.message?.includes('Failed to fetch dynamically imported module')) {
      const reloadKey = 'eb_reload_' + window.location.pathname;
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      // Only auto-reload once per 10 seconds to prevent loops
      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem(reloadKey, now.toString());
        window.location.reload();
        return;
      }
    }

    // Log to database for visibility — best-effort, never blocks
    try {
      supabase.from('error_logs' as any).insert({
        error_message: error.message,
        error_stack: error.stack?.slice(0, 2000) || null,
        component_stack: errorInfo.componentStack?.slice(0, 2000) || null,
        url: window.location.pathname,
        user_agent: navigator.userAgent,
        created_at: new Date().toISOString(),
      }).then(() => {});
    } catch {
      // Swallow — error logging should never cause more errors
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <h2 className="text-lg font-semibold text-foreground">
            {this.props.fallbackTitle || "Something went wrong"}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md">
            An unexpected error occurred. You can try refreshing or going back.
          </p>
          {this.state.error && (
            <details className="text-xs text-muted-foreground max-w-lg text-left bg-muted/50 rounded p-3 mt-2">
              <summary className="cursor-pointer font-medium">Error details</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">{this.state.error.message}</pre>
              {this.state.error.stack && (
                <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] opacity-60">{this.state.error.stack.split('\n').slice(0, 5).join('\n')}</pre>
              )}
            </details>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" /> Try Again
            </Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
